import React, { FC, PropsWithChildren, useContext } from 'react';
import {
  AccordionContext,
  Card,
  Accordion as RBAccordion,
  useAccordionButton,
} from 'react-bootstrap';

import classNames from 'classnames';
import { IconChevronUp, useTranslation } from 'crm-core';
import * as styles from './styles.module.scss';
import { AccordionItem } from './type';

type IAccordionProps = PropsWithChildren<{
  items: AccordionItem[];
  parentId?: string;
  action?: true;
  actionClassName?: string;
  indentSize?: 0 | 1 | 2 | 3 | 4 | 5;
  header?: React.ReactNode;
}>;

type IToogleProps = {
  children: React.ReactNode;
  eventKey: string;
  hasChildren?: boolean;
};

const Toggle: FC<IToogleProps> = ({ children, eventKey, hasChildren }) => {
  const { activeEventKey } = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(eventKey);

  return (
    <button
      type="button"
      className="accordion-header collapsed"
      onClick={decoratedOnClick}
    >
      <div className={styles.arrow}>
        {hasChildren && (
          <span
            className={classNames(
              'text-muted ms-auto',
              styles.transitionTransform,
              {
                [styles.rotate180]: !!activeEventKey?.includes(eventKey),
              },
            )}
          >
            <IconChevronUp size={14} />
          </span>
        )}
      </div>

      <>{children}</>
    </button>
  );
};

const Accordion: FC<IAccordionProps> = ({
  items,
  parentId = 'accordion',
  action = true,
  indentSize,
  header,
}) => {
  const { t } = useTranslation();
  return (
    <RBAccordion alwaysOpen flush id={parentId}>
      {!!header && (
        <Card className="border-0 bg-transparent">
          <Card.Header
            className={classNames(
              'd-flex p-0 align-items-center text-secondary',
              styles.header,
            )}
          >
            <div className="accordion-header py-2 collapsed">
              <div className={styles.arrow}></div>
              {header}
            </div>
            {action && <div className={styles.action}>{t('ACTION')}</div>}
          </Card.Header>
        </Card>
      )}
      {items.map((item) => {
        const eventKey = `${parentId}-${item.id}`;
        const childAccordionId = `${parentId}-child-${item.id}`;

        return (
          <Card className="border-0 bg-transparent" key={item.id}>
            <Card.Header className="d-flex p-0 align-items-center">
              <Toggle
                hasChildren={!!item?.children?.length || !!item?.content}
                eventKey={eventKey}
              >
                {item.title}
              </Toggle>
              {action && <div className={styles.action}>{item.action}</div>}
            </Card.Header>
            {(!!item?.children?.length || !!item?.content) && (
              <RBAccordion.Collapse eventKey={eventKey}>
                <Card.Body
                  className={classNames(
                    'p-0',
                    styles.level,
                    `ps-${indentSize || 0}`,
                  )}
                >
                  {item.content}
                  {!!item.children?.length && (
                    <Accordion
                      items={item.children}
                      parentId={childAccordionId}
                    />
                  )}
                </Card.Body>
              </RBAccordion.Collapse>
            )}
          </Card>
        );
      })}
    </RBAccordion>
  );
};

export default Accordion;
